import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Discipline, Tag } from '@sparro/app/core/models/supertag.model';

import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';

import { debounceTime, finalize, switchMap, tap } from 'rxjs/operators';

import { MetaDefinition } from '@angular/platform-browser';
import { JournalService } from '@journal/core/api/journal/journal.service';
import { environment } from '@journal/env/environment';
import { UserService } from '@sparro/app/core/api/user/user.service';
import { MetaService } from '@sparro/app/core/helpers/meta/meta.service';
import { Blog } from '@sparro/app/core/models/blog.model';
import { User } from '@sparro/app/core/models/user.model';
import { StorageHandlerService } from '@sparro/core/helpers/storage/storage-handler.service';
// import { ToastrService } from '@sparro/app/core/helpers/toastr/toastr.service';
import { SnackbarService } from '@sparro/core/helpers/toastr/snackbar.service';
import { UNI_CONSTANTS } from '@sparro/app/core/config/constants.config';

@Component({
  selector: 'app-journal-meta',
  templateUrl: './journal-meta.component.html',
  styleUrls: ['./journal-meta.component.scss']
})
export class JournalMetaComponent implements OnInit {
  blog: Blog;
  user: User;
  postUrl = null;
  defaultImage: string = UNI_CONSTANTS.NO_IMAGE.JOURNAL;

  loading: boolean = false;
  // showSpinner: boolean = false;
  canPublish: boolean = false;
  autoCompleteOption: boolean = false;
  hasAllTags: boolean = false;
  hasAllDisciplines: boolean = false;

  imageCount: number = 0;
  embedCount: number = 0;
  textCount: number = 0;

  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  disableDisciplines = false;
  // matAutocompleteDisabled = false;

  blogTypeArray = [];
  disciplines: Discipline[];
  filteredDisciplines: Discipline[];
  filteredTagsArray: Tag[];
  thumbnailDiscipline: Discipline;
  defaultBlogType: number;

  separatorKeysCodes: number[] = [ENTER, COMMA];

  blogTypeControl = new FormControl();
  disciplinesControl = new FormControl([]);
  tagSearchControl = new FormControl();

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild('disciplineInput') disciplineInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public metaService: MetaService,
    private location: Location,
    private userService: UserService,
    private journalService: JournalService,
    private changeDetectorRef: ChangeDetectorRef,
    public snackbarService: SnackbarService,
    public storageService: StorageHandlerService
  ) {
    this.postUrl = `${environment.postUrl}`;
  }

  ngOnInit(): void {
    this.initiateTags();
    this.loading = true;
    this.user = this.storageService.user;
    const uuid = this.location.path().split('/')[2]; // remove
    this.getAuthUser();
    this.getBlog(uuid);
    this.searchTag();
    this.searchDiscipline();
    this.updateMetaInfo();
  }

  ngAfterContentChecked(): void {
    // Fix for ExpressionChangedAfterItHasBeenCheckedError
    this.changeDetectorRef.detectChanges();
    if (this.blog && this.blog.disciplines.length >= 2) {
      this.disciplineInput.nativeElement.style.display = 'none';
    } else {
      if (this.disciplineInput) {
        this.disciplineInput.nativeElement.style.display = 'block';
      }
    }
  }

  getAuthUser(): void {
    this.userService.userInitialized.subscribe(
      (initialized: boolean) => {
        if (initialized) {
          this.userService.user.subscribe(
            (response: User) => {
              this.user = response;
            })
        }
      });
  }

  updateMetaInfo(): void {
    let metaInfo: MetaDefinition[] = [];
    if (this.user !== null) {
      const title = `Journal Editor - Add Description`
      metaInfo = [
        { name: 'title', content: title },
        { name: 'description', content: `Publish your own journal, blog, & news in architecture & design.` }
      ];
      this.metaService.updateTitle(title);
    }
    else {
      metaInfo = [
        { name: 'title', content: 'Login or Sign-up on UNI' },
        { name: 'description', content: 'Please login or sign-up on UNI to view this page' },
        { name: 'robots', content: 'noindex' },
      ];
    }
    metaInfo.forEach(meta => {
      this.metaService.updateMetaInfo(meta);
    });
    this.metaService.updateMeta(metaInfo);
    this.metaService.createCanonicalURL();
  }

  // Initialize
  initiateTags(): void {
    this.getDisciplines();
    this.getBlogType();
  }

  getBlog(slug: string): void {
    this.journalService.getBlog(slug).subscribe(
      (response: Blog) => {
        this.blog = response;
        this.getFilesCount();
        if (this.blog.tags.length === 5) {
          this.hasAllTags = true;
        }
        if (this.blog.disciplines.length === 2) {
          this.hasAllDisciplines = true;
        }
        this.defaultBlogType = this.blog.type.id;
        if (this.blog.disciplines.length > 0 && this.blog.image) {
          this.journalService.setBlog(this.blog);
          this.canPublish = true;
          this.thumbnailDiscipline = this.blog.disciplines[0];
        }
        this.loading = false;
      }, (err) => {
        console.log(err);
      });
  }

  getDisciplines(): void {
    this.journalService.allDisciplines().subscribe(
      (disciplines: Discipline[]) => {
        this.disciplines = disciplines;
        this.filteredDisciplines = this.disciplines;
      }, (err) => {
        console.log(err);
      });
  }

  getBlogType(): void {
    this.journalService.getBlogTypes().subscribe(
      (blogType: any) => {
        this.blogTypeArray = blogType.results;
      }, (err) => {
        console.log(err);
      });
  }

  media(event): void {
    this.blog.image = event.image;
    this.checkPublishable();
  }

  // Tags
  searchTag(): void {
    this.tagSearchControl.valueChanges.pipe(
      debounceTime(500),
      tap((value) => {
        this.filteredTagsArray = [];
        // this.showSpinner = true;
      }),
      switchMap(
        (value: any) => (
          this.journalService.searchSuperTag(value).pipe(
            finalize(() => {
              // this.showSpinner = false;
            }),
          )
        )
      )).subscribe(
        (result: any) => {
          if (result === null) {
            // this.showSpinner = false;
          }
          this.filteredTagsArray = result.results;
        });
  }

  searchDiscipline(): void {
    this.disciplinesControl.valueChanges.subscribe(
      (discipline: any) => {
        if (discipline === null) {
          this.filteredDisciplines = this.disciplines.slice();
        } else {
          this._filterDiscipline(discipline);
        }
      });
  }

  _filterDiscipline(discipline): void {
    const filterValue = discipline.name ? discipline.name.toLowerCase() : discipline.toLowerCase();
    this.filteredDisciplines = this.disciplines.filter(discipline => discipline.name.toLowerCase().indexOf(filterValue) === 0);
  }

  addDiscipline(event: MatAutocompleteSelectedEvent): void {
    this.autoCompleteOption = true;
    const data = {
      discipline: event.option.value
    }
    const disciplineExist = this.blog.disciplines.some(element => element.id === event.option.value.id);
    if (!disciplineExist) {
      this.journalService.addDiscipline(this.blog.uuid, data).subscribe(
        (response: any) => {
          if (response.success) {
            this.snackbarService.success(`Discipline added successfully.`);
            this.blog.disciplines.push(event.option.value);
            this.disciplineInput.nativeElement.value = '';
            this.disciplinesControl.setValue(null);
            this._checkDisabled();
            if (this.blog.disciplines.length === 1 && !this.blog.image) {
              this.connectDisciplineThumbnail();
            }
            this.canPublish = true;
            this.journalService.setBlog(this.blog);
          }
        },
        (error) => {
          console.log(error);
        })
    } else {
      this.snackbarService.error(`Discipline already exists. Please select another Discipline.`);
    }
  }

  removeDiscipline(disciplineObj): void {
    const data = {
      discipline: disciplineObj
    }
    this.journalService.removeDiscipline(this.blog.uuid, data).subscribe(
      (response: any) => {
        this.snackbarService.success(`Disciplined removed successfully.`);
        const removedDiscipline = response.discipline;
        this.blog.disciplines = this.blog.disciplines.filter((item, index) => {
          return item.id !== removedDiscipline;
        });
        if (this.blog.disciplines.length === 0) {
          this.canPublish = false;
          this.journalService.setBlog(this.blog);
        }
        if (removedDiscipline.icon === this.blog.image) {
          this.replaceThumbnail();
        }
        // this.disciplineInput.nativeElement.disabled = false;
        // this.hasAllDisciplines = false;
        this.disciplineInput.nativeElement.value = '';
        this._checkDisabled();
      },
      (error) => {
        console.log(error);
      });
  }

  addTag(event: MatAutocompleteSelectedEvent): void {
    this.autoCompleteOption = true;
    const tagObj = event.option.value;
    // console.log(tagObj);

    const data = {
      tag: {
        id: tagObj.tags.id,
        name: tagObj.name
      },
    }
    const tagExists = this.blog.tags.some(
      (element, index, array) => {
        return element.id === tagObj.id;
      });
    if (!tagExists) {
      this.journalService.addTag(this.blog.uuid, data).subscribe(
        (response: any) => {
          this.blog.tags.push(response);
          this.snackbarService.success(`Tag added successfully.`);
          this.tagInput.nativeElement.value = '';
          this.tagSearchControl.setValue(null);
          if (this.blog.tags.length === 5) {
            this.hasAllTags = true;
            this.tagInput.nativeElement.disabled = true;
          }
        }, (err) => {
          console.log(err);
        });
    } else {
      this.snackbarService.error(`Tag already selected. Please select a different tag.`);
    }
  }

  removeTag(tag) {
    // console.log(tag);
    const data = {
      tag: tag,
    }
    this.journalService.removeTag(this.blog.uuid, data).subscribe(
      (response: any) => {
        this.blog.tags = this.blog.tags.filter((item, index) => {
          return item.id !== tag.id;
        });
        this.snackbarService.success(`Tag removed successfully.`)
      }, (err) => {
        console.log(err);
      });
    this.tagInput.nativeElement.disabled = false;
    this.hasAllTags = false;
  }

  createTag(event: MatChipInputEvent): void {
    if (this.autoCompleteOption) {
      this.autoCompleteOption = false;
      return;
    }
    else if ((event.value || '').trim() && !this.matAutocomplete.isOpen) {
      const data = {
        tag: { name: event.value.trim(), }
      }
      if (event.input) {
        event.input.value = '';
      }
      this.journalService.addTag(this.blog.uuid, data).subscribe(
        (response: any) => {
          this.blog.tags.push(response);
          this.tagInput.nativeElement.value = '';
          this.tagSearchControl.setValue(null);
        }, (err) => {
          console.log(err);
        });
    }
    if (this.blog.tags.length === 5) {
      this.hasAllTags = true;
      this.tagInput.nativeElement.disabled = true;
    }
  }

  changeBlogType(type: any): void {
    if (type.name === this.blog.type.name) {
      return;
    }
    const data = {
      type: type.id,
    }
    this.journalService.changeBlogType(this.blog.uuid, data).subscribe(
      (response: any) => {
        // console.log(response);
        this.blog.type = response;
      }, (err) => {
        console.log(err);
      });
  }

  // Thumbnail
  connectDisciplineThumbnail(): void {
    if (this.blog.disciplines.length > 0) {
      this.thumbnailDiscipline = this.blog.disciplines[0];
      const data = {
        discipline: this.thumbnailDiscipline.id,
      }
      this.journalService.connectDisciplineThumbnail(this.blog.uuid, data).subscribe(
        (response) => {
          this.blog.image = this.thumbnailDiscipline.icon;
        }, (err) => {
          console.log(err);
        });
    }
  }

  replaceThumbnail(): void {
    this.journalService.removeDisciplineThumbnail(this.blog.uuid).subscribe(
      (response) => {
        // console.log(response);
        if (this.blog.disciplines.length > 0) {
          this.connectDisciplineThumbnail();
        }
      }, (err) => {
        console.log(err);
      });
  }

  checkPublishable() {
    this.journalService.setBlog(this.blog);
    this.canPublish = Boolean(this.blog.disciplines.length > 0);
  }

  _checkDisabled(): void {
    if (this.blog.disciplines.length >= 2) {
      this.hasAllDisciplines = true;
      this.disciplineInput.nativeElement.disabled = true;
    } else {
      this.hasAllDisciplines = false;
      this.disciplineInput.nativeElement.disabled = false;
    }
  }

  getFilesCount(): void {
    let content = this.blog.content;
    if (content) {
      const imageRegex = /<img.*?>/g
      const embedRegex = /<iframe.*?>/g
      // console.log(content.match(imageRegex), content.match(embedRegex), content)
      const imageMatches = content.match(imageRegex);
      const embedMatches = content.match(embedRegex);
      this.imageCount = imageMatches ? imageMatches.length : 0;
      this.embedCount = embedMatches ? embedMatches.length : 0;
      content = content.replace(/<[^>]*>/g, ' ');
      this.textCount = content.trim().split(' ').length;
    } else {
      this.imageCount = this.embedCount = 0;
      this.textCount = 0;
    }
  }

  publishBlog(): void {
    if (!this.canPublish) {
      return;
    }
    else {
      this.journalService.publishBlog(this.blog.uuid).subscribe(
        (response) => {
          const success = response['success'];
          if (success) {
            const slug = response['slug'];
            window.location.href = `${this.postUrl}${slug}`;
          }
        }, (err) => {
          console.log(err);
        });
    }
  }

  goBack() {
    this.router.navigate(['../'], {relativeTo: this.route,});
  }
}
